import { Form, Input, Modal, notification, Select, Typography } from 'antd';
import React, { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  Organization,
  OrganizationType,
} from 'protocol/model/dto_organization_pb';

import { organizationTypeDefaultMessages } from '@/shared/config/i18n';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  DISTRIBUTORS_FILTER,
  DSOS_FILTER,
  OrganizationSearchSelect,
  organizationsMessages,
} from '@/entities/organizations';
import { userModel } from '@/entities/user';

const { Title } = Typography;

type CreateOrganizationModalProps = {
  canManageDistributors: boolean;
  canManageDSO: boolean;
  isOpen: boolean;
  onCloseHandler: () => void;
};

type CreateOrganizationFormFields = {
  companyName: string;
  companyType: OrganizationType;
  dsoParentID?: string;
};

type OptionsType = {
  value: OrganizationType;
  label: string;
};

export const CreateOrganizationModal: FC<CreateOrganizationModalProps> = (
  props,
) => {
  const { canManageDistributors, canManageDSO, isOpen, onCloseHandler } = props;

  const dispatch = useAppDispatch();

  const { formatMessage } = useIntl();

  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const isDistributorAdmin = useAppSelector(
    userModel.selectors.selectIsDistributorAdmin,
  );
  const adminDistributorOrganization = useAppSelector(
    userModel.selectors.selectAdminDistributorOrganization,
  );

  const [form] = Form.useForm<CreateOrganizationFormFields>();
  const dsoParentID = Form.useWatch('dsoParentID', form);

  const [loading, setLoading] = useState<boolean>(false);
  const [pickedDistributor, setPickedDistributor] = useState<Organization>();

  // The modal mounts together with the page and is never unmounted, so a closed
  // one keeps whatever the previous run left behind — including the affiliation,
  // which would then be sent for the next organization.
  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
      setPickedDistributor(undefined);
    }
  }, [isOpen, form]);

  // A distributor admin onboards clients only under their own distributor, so
  // the field is pinned to it instead of being pickable.
  const distributor = adminDistributorOrganization ?? pickedDistributor;

  const organizationTypeOptions: OptionsType[] = [
    {
      label: formatMessage(
        organizationTypeDefaultMessages[
          OrganizationType.OrganizationType_Dental
        ],
      ),
      value: OrganizationType.OrganizationType_Dental,
    },
    {
      label: formatMessage(
        organizationTypeDefaultMessages[
          OrganizationType.OrganizationType_NonDental
        ],
      ),
      value: OrganizationType.OrganizationType_NonDental,
    },
  ];

  // The contract carries a single affiliation, so the two pickers stay
  // mutually exclusive and only the filled one is sent.
  const getAffiliation = () => {
    if (distributor) {
      return { case: 'DistributorParentID' as const, value: distributor.ID };
    }

    if (dsoParentID) {
      return { case: 'DSOParentID' as const, value: dsoParentID };
    }

    return undefined;
  };

  const handleSubmit = () =>
    form.validateFields().then(async (values) => {
      const { companyName, companyType } = values;

      try {
        setLoading(true);

        const { Organization: createdOrganization } = await dispatch(
          userModel.thunks.createOrganization({
            Owner: { case: 'OwnerID', value: currentUser.ID },
            Name: companyName,
            Type: companyType,
            Affiliation: getAffiliation(),
          }),
        ).unwrap();

        if (createdOrganization?.ID) {
          dispatch(
            userModel.actions.addOrganizationName({
              id: createdOrganization.ID,
              name: createdOrganization.Name,
            }),
          );
        }

        onCloseHandler();
      } catch (error) {
        notification.error({ message: error?.message });
      } finally {
        setLoading(false);
      }
    });

  return (
    <Modal
      open={isOpen}
      centered
      closable={false}
      okText={formatMessage({ id: 'global.add', defaultMessage: 'Add' })}
      cancelText={formatMessage({
        id: 'global.cancel',
        defaultMessage: 'Cancel',
      })}
      onCancel={onCloseHandler}
      onOk={handleSubmit}
      confirmLoading={loading}
    >
      <Title level={3}>
        {formatMessage({
          id: 'organization.addOrganization.title',
          defaultMessage: 'Add organization',
        })}
      </Title>

      <Title level={5}>
        {formatMessage(
          {
            id: 'organization.addOrganization.message',
            defaultMessage:
              'You are going to add organization for a client {client} ({clientEmail})',
          },
          {
            client: `${currentUser?.PersonalData?.FirstName} ${currentUser?.PersonalData?.LastName}`,
            clientEmail: currentUser?.PersonalData?.Emails[0],
          },
        )}
      </Title>

      <Form
        name="createOrganizationForm"
        form={form}
        layout="vertical"
        autoComplete="off"
      >
        <Form.Item
          name="companyName"
          label={formatMessage({
            id: 'organization.create.companyName.label',
            defaultMessage: 'Company name',
          })}
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'forms.required',
                defaultMessage: 'Field is required',
              }),
            },
          ]}
        >
          <Input
            placeholder={formatMessage({
              id: 'organization.create.companyName',
              defaultMessage: 'Organization name',
            })}
          />
        </Form.Item>

        <Form.Item
          name="companyType"
          label={formatMessage({
            id: 'organization.create.companyType.label',
            defaultMessage: 'Company type',
          })}
          rules={[
            {
              required: true,
              message: formatMessage({
                id: 'forms.required',
                defaultMessage: 'Field is required',
              }),
            },
          ]}
        >
          <Select options={organizationTypeOptions} />
        </Form.Item>

        <Form.Item
          name="dsoParentID"
          label={formatMessage(organizationsMessages.parentDSOLabel)}
        >
          <OrganizationSearchSelect
            disabled={!canManageDSO || Boolean(distributor)}
            filter={DSOS_FILTER}
            placeholder={formatMessage(organizationsMessages.searchPlaceholder)}
          />
        </Form.Item>

        {/* WARN: the field is intentionally left out of the form state. The
        modal mounts together with the page, so `initialValues` would resolve
        before the user stream arrives and the distributor admin prefill would
        silently be lost. */}
        <Form.Item
          label={formatMessage({
            id: 'organization.distributor',
            defaultMessage: 'Distributor',
          })}
        >
          <OrganizationSearchSelect
            disabled={
              isDistributorAdmin ||
              !canManageDistributors ||
              Boolean(dsoParentID)
            }
            filter={DISTRIBUTORS_FILTER}
            placeholder={formatMessage(organizationsMessages.searchPlaceholder)}
            selectedOrganization={distributor}
            value={distributor?.ID}
            onChange={(_, organization) => setPickedDistributor(organization)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
